
import { Component, OnInit } from '@angular/core';
import { IonicModule, AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { RouterModule } from '@angular/router';
import { addIcons } from 'ionicons';
import { AppHeaderComponent } from '../shared/app-header/app-header.component';
import { AuthService } from '../shared/auth.service';
import { CommonModule } from '@angular/common';
import { environment } from '../../environments/environment';
import { 
  informationCircleOutline, 
  personCircleOutline, 
  starOutline, 
  heartOutline,
  gridOutline,
  addCircleOutline,
  homeOutline
} from 'ionicons/icons';


@Component({
  selector: 'app-tabs',
  standalone: true,
  imports: [IonicModule, RouterModule, AppHeaderComponent, CommonModule],
  templateUrl: './tabs.page.html'
})
export class TabsPage implements OnInit {
  isAdmin: boolean = false;
  isProfileCompleted: boolean = false;
  hasHobbies: boolean = false;
  
  constructor(
    private authService: AuthService,
    private router: Router,
    private alertController: AlertController
  ) {
    addIcons({
      informationCircleOutline,
      personCircleOutline,
      starOutline,
      heartOutline,
      gridOutline,
      addCircleOutline,
      homeOutline
    });
  }
  
  ngOnInit() {
    this.checkUserRole();
    this.checkHobbyCount();
    
    // Subscribe to profile completion changes to update tab states
    this.authService.profileCompleted$.subscribe(isCompleted => {
      console.log('[TABS] Profile completed status changed to:', isCompleted);
      this.isProfileCompleted = isCompleted;
      
      // Check hobby count when profile status changes
      if (isCompleted) {
        this.checkHobbyCount();
      }
    });
    
    // Fix for production: force ion-router-outlet to have height
    setTimeout(() => {
      const outlet = document.querySelector('ion-router-outlet');
      if (outlet && outlet instanceof HTMLElement) {
        const computedHeight = window.getComputedStyle(outlet).height;
        if (computedHeight === '0px' || computedHeight === '0') {
          console.log('[TABS] Fixing ion-router-outlet height in production');
          outlet.style.setProperty('height', '100%', 'important');
          outlet.style.setProperty('flex', '1 1 auto', 'important');
          outlet.style.setProperty('display', 'block', 'important');
        }
      }
      
      // Ensure tab bar is scrollable and all tabs are accessible
      this.ensureTabsVisible();
    }, 100);
  }
  
  ionViewWillEnter() {
    // Refresh hobby count whenever tabs are shown
    this.checkHobbyCount();
  }
  
  ensureTabsVisible() {
    const tabBar = document.querySelector('ion-tab-bar');
    if (tabBar) {
      // Check if tab bar is scrollable
      const isScrollable = tabBar.scrollWidth > tabBar.clientWidth;
      console.log('[TABS] Tab bar scrollable:', isScrollable, 'scrollWidth:', tabBar.scrollWidth, 'clientWidth:', tabBar.clientWidth);
      
      // If scrollable, scroll to show rightmost tabs slightly
      if (isScrollable) {
        // Scroll to center the tabs
        const scrollAmount = (tabBar.scrollWidth - tabBar.clientWidth) / 2;
        tabBar.scrollLeft = scrollAmount;
        console.log('[TABS] Centered tab bar scroll to:', scrollAmount);
      }
    }
  }
  
  async checkUserRole() {
    console.log('[TABS] checkUserRole() started');
    
    try {
      const response = await fetch(`${environment.baseUrl}/api/user`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const userData = await response.json();
        this.isAdmin = userData.role === 'admin';
        this.isProfileCompleted = userData.isProfileCompleted || false;
        
        // Update AuthService with profile completion status
        this.authService.setProfileCompleted(this.isProfileCompleted);
        
        console.log('[TABS] ✓ User profile loaded:', { 
          isAdmin: this.isAdmin, 
          isProfileCompleted: this.isProfileCompleted,
          userData: userData 
        });
      } else {
        console.warn('[TABS] Failed to load user profile, using defaults. Status:', response.status);
        this.isAdmin = false;
        this.isProfileCompleted = false;
        this.authService.setProfileCompleted(false);
      }
    } catch (error) {
      console.error('[TABS] Error checking user role:', error);
      this.isAdmin = false;
      this.isProfileCompleted = false;
      this.authService.setProfileCompleted(false);
    }
  }
  
  async checkHobbyCount() {
    try {
      const response = await fetch(`${environment.baseUrl}/api/userHobbies/count`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const hobbyCount = data.hobbyCount || 0;
        this.hasHobbies = hobbyCount > 0;
        console.log('[TABS] Hobby count:', hobbyCount, 'hasHobbies:', this.hasHobbies);
      }
    } catch (error) {
      console.error('[TABS] Error checking hobby count:', error);
      this.hasHobbies = false;
    }
  }
  
  handleTabClick(path: string, event?: Event) {
    console.log('[TABS] Tab clicked:', path, 'Profile completed:', this.isProfileCompleted);
    
    // If profile is not completed, only allow profile tab
    if (!this.isProfileCompleted && path !== 'profile') {
      console.log('[TABS] Tab disabled - complete profile first');
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    
    // If profile is completed but no hobbies, only allow profile and hobbies tabs
    // Note: We'll check hobby count in the actual navigation
    
    this.navigateToTab(path, event);
  }
  
  navigateToTab(path: string, event?: Event) {
    if (event) {
      event.preventDefault();
    }
    
    console.log('[TABS] Navigating to:', `/tabs/${path}`);
    
    this.router.navigate([`/tabs/${path}`]).then((success) => {
      console.log('[TABS] Navigation success:', success);
      // Force visibility after navigation in production
      setTimeout(() => {
        const pageComponents = document.querySelectorAll('app-dashboard, app-profile, app-matches, app-hobbies, app-my-hobbies');
        pageComponents.forEach((el: any) => {
          if (el && el.style) {
            el.style.opacity = '1';
            el.style.visibility = 'visible';
          }
        });
      }, 50);
    }).catch((error) => {
      console.error('[TABS] Navigation error:', error);
    });
  }
}


